import { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import {
  ContentDecisionResponse,
  OptDecision,
} from "common/api/decimal/methods/course.ts";
import useToasts from "routes/root/core-ui/toast/useToasts.ts";
import { useContentDecisionFetcher } from "../actions/updateContentDecisionAction.ts";
import DefaultErrorToast from "../common/DefaultErrorToast.tsx";
import Toast from "common/core-ui/Toast.tsx";
import AssetCoverToast from "common/features/asset/AssetCoverToast.tsx";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";
import { getDaysRemaining } from "./timeHelper.tsx";
import { CourseMaterial } from "./coursesLoader.ts";

export default function useChangeContentDecision({
  courseMaterial,
  afterDecisionChange,
  selectProgram,
}: {
  courseMaterial: CourseMaterial;
  afterDecisionChange?: () => void;
  selectProgram?: () => void;
}) {
  const { vbid, title, verbaLicense, courseId, optOutDeadline } =
    courseMaterial;
  const deadline = optOutDeadline ?? "";
  const fetcher = useContentDecisionFetcher();
  const { showToast } = useToasts();
  const verbaLicenseId = verbaLicense?.id;

  const showBillingUpdate = useCallback(
    ({ decision }: ContentDecisionResponse) => {
      const getTitle = () => {
        if (decision === "opt_in") {
          return (
            <FormattedMessage
              defaultMessage="You have opted in and gained access to {title}."
              values={{
                title,
              }}
            />
          );
        } else {
          return (
            <FormattedMessage
              defaultMessage="You have opted out and lost access to {title}."
              values={{
                title,
              }}
            />
          );
        }
      };
      showToast(
        <AssetCoverToast vbid={vbid} title={getTitle()}>
          <FormattedMessage defaultMessage="Billing will be updated in your student account." />
        </AssetCoverToast>
      );
    },
    [showToast, title, vbid]
  );

  const showProgramUpdate = useCallback(
    ({ previousProgram, updatedProgram }: ContentDecisionResponse) => {
      if (
        previousProgram === "no_program" &&
        updatedProgram === "inclusive_access"
      ) {
        showToast(
          <Toast
            type="success"
            title={
              <FormattedMessage defaultMessage="You are now in the Inclusive Access Program." />
            }
          />
        );
      }
      if (
        previousProgram === "inclusive_access" &&
        updatedProgram === "no_program"
      ) {
        showToast(
          <Toast
            type="success"
            title={
              <FormattedMessage defaultMessage="You have opted out of all course materials." />
            }
          >
            <FormattedMessage
              defaultMessage="{daysRemaining, plural, =0 {Today is the last day to change your program.} =1 {You have 1 day to change your program.} other {You have # days to change your program.}}"
              values={{
                daysRemaining: getDaysRemaining(deadline),
              }}
            />
            {selectProgram && (
              <BaseLinkOrButton
                onClick={() => selectProgram()}
                className="vst-link"
              >
                <FormattedMessage defaultMessage="Select Programs" />
              </BaseLinkOrButton>
            )}
          </Toast>
        );
      }
    },
    [showToast, selectProgram, deadline]
  );

  useEffect(() => {
    if (fetcher.state === "loading" && fetcher.data) {
      if (fetcher.data?.data) {
        showBillingUpdate(fetcher.data.data);
        showProgramUpdate(fetcher.data.data);
      } else {
        showToast(<DefaultErrorToast />);
      }
      if (afterDecisionChange) {
        afterDecisionChange();
      }
    }
  }, [
    fetcher,
    showToast,
    showBillingUpdate,
    showProgramUpdate,
    afterDecisionChange,
  ]);

  const changeOptDecision = useCallback(
    (decision: OptDecision) => {
      return fetcher
        .submit(
          {
            courseId,
            verbaLicenseId,
            decision,
            reasonId: "0",
          },
          {
            action: `/my-courses/content-decision`,
            method: "PUT",
          }
        )
        .then(() => {})
        .catch(() => {});
    },
    [courseId, fetcher, verbaLicenseId]
  );

  const isOptDecisionProcessing = fetcher.state !== "idle";

  const contentOptIn = useCallback(() => {
    return changeOptDecision("opt_in");
  }, [changeOptDecision]);

  const contentOptOut = useCallback(() => {
    return changeOptDecision("opt_out");
  }, [changeOptDecision]);

  return useMemo(
    () => ({
      contentOptIn,
      contentOptOut,
      isOptDecisionProcessing,
    }),
    [contentOptIn, contentOptOut, isOptDecisionProcessing]
  );
}
